"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import PixelCompatibleCard from "./PixelCompatibleCard";
import IphoneCompatibleCard from "./IphoneCompatibleCard";
import SamsungCompatibleCard from "./SamsungCompatibleCard";

const DISPLAY_DURATION = 4000; // time card stays active
const EXIT_DURATION = 2200; // full exit sequence

const cards = [
  { key: "pixel", name: "Pixel", Component: PixelCompatibleCard },
  { key: "iphone", name: "iPhone", Component: IphoneCompatibleCard },
  { key: "samsung", name: "Samsung", Component: SamsungCompatibleCard },
] as const;

function CompatibleCardSwitcher() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [phase, setPhase] = useState<"enter" | "exit">("enter");

  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  // IntersectionObserver: detect when section enters viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "0px 0px -60px 0px",
        threshold: 0.15,
      },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Precise sequential cycle: DISPLAY_DURATION -> EXIT_DURATION -> NEXT CARD
  useEffect(() => {
    if (!isVisible) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    if (phase === "enter") {
      timeoutId = setTimeout(() => {
        setPhase("exit");
      }, DISPLAY_DURATION);
    } else {
      timeoutId = setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % cards.length);
        setPhase("enter");
      }, EXIT_DURATION);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isVisible, phase]);

  // Manual navigation handlers for swipe & buttons
  const goToNextCard = () => {
    setActiveIndex((prev) => (prev + 1) % cards.length);
    setPhase("enter");
  };

  const goToPrevCard = () => {
    setActiveIndex((prev) => (prev - 1 + cards.length) % cards.length);
    setPhase("enter");
  };

  const goToCard = (index: number) => {
    if (index === activeIndex) return;
    setActiveIndex(index);
    setPhase("enter");
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null)
      return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const deltaY = e.changedTouches[0].clientY - touchStartYRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;

    // Detect horizontal swipe with threshold of 40px
    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        goToNextCard(); // Swipe Left -> Next
      } else {
        goToPrevCard(); // Swipe Right -> Previous
      }
    }
  };

  const { Component } = cards[activeIndex];

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: "pan-y" }}
      className={`relative z-10 w-full select-none ${
        isVisible ? "is-visible" : ""
      }`}
    >
      <div
        key={cards[activeIndex].key}
        className={`compatible-card-slide w-full ${
          phase === "enter" ? "compatible-card-enter" : "compatible-card-exit"
        }`}
      >
        <Component />
      </div>

      {/* Mobile & Tablet Carousel Controls */}
      <div className="relative z-20 flex items-center justify-center gap-4 md:hidden">
        <button
          type="button"
          onClick={goToPrevCard}
          aria-label="Previous device"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 active:scale-95"
        >
          <ChevronLeft size={18} />
        </button>

        {/* Carousel indicator dots */}
        <div className="flex items-center gap-2">
          {cards.map((card, idx) => (
            <button
              key={card.key}
              type="button"
              onClick={() => goToCard(idx)}
              aria-label={`Go to ${card.name}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === activeIndex
                  ? "w-6 bg-white"
                  : "w-2 bg-white/30 hover:bg-white/50"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={goToNextCard}
          aria-label="Next device"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 active:scale-95"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

export default CompatibleCardSwitcher;
